import React, { useEffect, useState } from 'react';
import AddButton from 'components/v2/buttons/AddButton';
import List from 'components/v2/list/List';
import { ConfirmActionModal } from 'components/ui/modal';
import { useTranslation } from 'react-i18next';
import { useUpdateQuickPay } from 'hooks/quick-pay/useContracts';
import LoadingIndicator from 'components/v2/LoadingIndicator';
import { showToast } from 'stores/ToastStore';
import SessionStore from 'stores/SessionStore';
import ContractNotApplied from './ContractNotApplied';
import ContractListItem from './ContractListItem';
import AddContractForm from './AddContractForm';
const VALID_QUICK_PAY_DISCOUNT_RATES = [1, 1.5, 2, 2.5, 3];
export default function Active({ id, loading, activeContracts = [], scheduledContracts = [], termsAndConditions = [], }) {
    const [showAddContractForm, setShowAddContractForm] = useState(false);
    const [showConfirmDeleteSettingModal, setShowConfirmDeleteSettingModal] = useState(false);
    const [deleteContract, setDeleteContract] = useState(null);
    const [saving, setSaving] = useState(false);
    const [activeTermsAndConditions, setActiveTermsAndConditions] = useState([]);
    const { t } = useTranslation();
    useEffect(() => {
        setActiveTermsAndConditions(termsAndConditions
            .filter((t) => t.payment_method === 'manual' &&
            VALID_QUICK_PAY_DISCOUNT_RATES.includes(t.rate.value) &&
            t.duration.type === 'days' &&
            t.duration.value === 1)
            .sort((a, b) => a.rate.value - b.rate.value)
            .map((item) => (Object.assign(Object.assign({}, item), { displayText: `${item.duration.value} ${item.duration.type}, ${item.rate.value} ${item.rate.type}, ${item.payment_method}` }))));
    }, [termsAndConditions]);
    const hasPermissionForActions = SessionStore.hasFinanceAdminRole();
    const canCreateNewContract = (scheduledContracts.length < 1 && !loading) || activeContracts.length === 0;
    const onDeleteSuccess = () => {
        setDeleteContract(null);
        handleCloseConfirmDeleteSettingModal();
        setSaving(false);
        showToast(t('DELETE_QUICKPAY_SUCCESS'), t('DELETE_QUICKPAY_SUCCESS'), 'success');
    };
    const onDeleteFail = () => {
        setSaving(false);
        showToast(t('DELETE_QUICKPAY_FAILED'), t('DELETE_QUICKPAY_FAILED'), 'error');
    };
    const { mutation: deleteQuickpay } = useUpdateQuickPay(onDeleteSuccess, onDeleteFail);
    function renderAddContractForm() {
        if (!showAddContractForm) {
            return null;
        }
        return (React.createElement("div", { "data-testid": "quickpay-add-contract", className: "" },
            React.createElement(AddContractForm, { data: {}, onCancel: () => setShowAddContractForm(false), carrier: id, activeTermsAndConditions: activeTermsAndConditions }),
            React.createElement("hr", { className: "my-8" })));
    }
    async function handleDelete() {
        setSaving(true);
        await deleteQuickpay.mutateAsync({
            contractId: deleteContract._id,
            updates: { active: false },
        });
    }
    function renderHeading() {
        return (React.createElement("div", { className: "mb-6 flex items-center space-x-3" },
            React.createElement("h3", { "data-testid": "quickpay-title", className: "" }, t('QUICK_PAY_SETTINGS')),
            hasPermissionForActions && canCreateNewContract && (React.createElement(AddButton, { dataTestId: "quickpay-add-button", size: "small", onClick: () => setShowAddContractForm(true) }))));
    }
    function renderCurrentSettings() {
        if (activeContracts.length > 0) {
            return (React.createElement(List, { testDataId: "quickpay-current-list", title: t('CURRENT'), className: "mb-2" }, activeContracts.map((contract, i) => {
                return (React.createElement(ContractListItem, { actionsEnabled: hasPermissionForActions, data: contract, carrier: id, key: i, onDelete: handleShowConfirmDeleteSettingModal, activeTermsAndConditions: activeTermsAndConditions }));
            })));
        }
        if (!showAddContractForm) {
            return (React.createElement(ContractNotApplied, { showEmptyMessage: !scheduledContracts.length }));
        }
        return null;
    }
    function handleShowConfirmDeleteSettingModal(setting) {
        setDeleteContract(setting);
        setShowConfirmDeleteSettingModal(true);
    }
    function handleCloseConfirmDeleteSettingModal() {
        setShowConfirmDeleteSettingModal(false);
    }
    function renderUpcoming() {
        if (scheduledContracts.length === 0) {
            return React.createElement("p", null, t('EMPTY_QUICKPAY_UPCOMING'));
        }
        if (scheduledContracts.length > 0) {
            return (React.createElement(React.Fragment, null,
                React.createElement(List, { testDataId: "quickpay-upcoming-list", title: t('UPCOMING'), className: "mb-6" }, scheduledContracts.map((contract, i) => {
                    return (React.createElement(ContractListItem, { actionsEnabled: hasPermissionForActions, data: contract, carrier: id, key: i, onDelete: handleShowConfirmDeleteSettingModal, activeTermsAndConditions: activeTermsAndConditions }));
                })),
                React.createElement(ConfirmActionModal, { title: t('DELETE_QUICKPAY_QUESTION'), isOpen: showConfirmDeleteSettingModal, onClose: handleCloseConfirmDeleteSettingModal, onConfirm: handleDelete, confirmActionColor: "red", actionDisabled: saving }, t('DELETE_QUICKPAY_MSG'))));
        }
    }
    return loading ? (React.createElement(LoadingIndicator, null)) : (React.createElement("div", { "data-testid": "quickpay-page", className: "w-full" },
        renderHeading(),
        renderAddContractForm(),
        renderCurrentSettings(),
        scheduledContracts.length > 0 && (React.createElement(React.Fragment, null,
            React.createElement("hr", { className: "my-8" }),
            renderUpcoming()))));
}
